/**
 * HoVisibilityPanel - the homeowner's public/private choice for a project.
 *
 * The API only flips visibility while a project is in progress, so the switch
 * appears only then. Before the project starts the panel says when the choice
 * takes effect; once it is completed or cancelled there is nothing to choose.
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { queryKeys } from "@/lib/query-keys";
import {
	type Project,
	toggleProjectPublic,
} from "../../services/projects-api.service";
import { Eyebrow, FloatPanel } from "./design-primitives";

const BEFORE_START = ["draft", "seeking_designer", "awaiting_funding"];

interface HoVisibilityPanelProps {
	projectId: string;
	status: string;
	isPublic: boolean;
	/** Reads the project again, so the switch shows what the server holds. */
	onRefetch: () => Promise<unknown>;
}

export function HoVisibilityPanel({
	projectId,
	status,
	isPublic,
	onRefetch,
}: HoVisibilityPanelProps): React.ReactElement | null {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [isToggling, setIsToggling] = React.useState(false);
	// Set synchronously, before the first await: state only updates on the next
	// render, so two clicks in one tick would both pass a check of `isToggling`.
	const inFlight = React.useRef(false);

	if (status !== "in_progress") {
		if (!BEFORE_START.includes(status)) return null;
		return (
			<FloatPanel elev={1} padding={18}>
				<Eyebrow style={{ fontSize: 8 }}>
					{t("projectVisibility.title").toUpperCase()}
				</Eyebrow>
				<p className="mt-2 font-body text-sm text-dashboard-muted-dark">
					{t("projectVisibility.beforeStart")}
				</p>
			</FloatPanel>
		);
	}

	const refetchQuietly = async (): Promise<void> => {
		try {
			await onRefetch();
		} catch {
			// the toast already reported the toggle; a failed read changes nothing
		}
	};

	const handleToggle = async (): Promise<void> => {
		if (inFlight.current) return;
		inFlight.current = true;
		setIsToggling(true);
		try {
			const result = await toggleProjectPublic(projectId);
			if (result.success && result.project) {
				// The server's answer goes into the cached project at once, so the
				// switch is right even if the read that follows fails. Only the flag
				// is written: the cached project carries more than the toggle returns.
				const isNowPublic = result.project.isPublic ?? false;
				queryClient.setQueryData<Project | null>(
					queryKeys.projects.detail(projectId),
					(cached) => (cached ? { ...cached, isPublic: isNowPublic } : cached),
				);
				toast.success(
					isNowPublic
						? t("projectVisibility.nowPublic")
						: t("projectVisibility.nowPrivate"),
				);
			} else {
				toast.error(result.error || t("projectVisibility.failed"));
			}
		} catch {
			toast.error(t("projectVisibility.failed"));
		} finally {
			// Success and failure both end in a read: after a success it brings the
			// project in, after a refusal (a 409 included) it shows the real value.
			await refetchQuietly();
			inFlight.current = false;
			setIsToggling(false);
		}
	};

	return (
		<FloatPanel elev={1} padding={18}>
			<div className="flex items-start justify-between gap-4">
				<div>
					<Eyebrow style={{ fontSize: 8 }}>
						{t("projectVisibility.title").toUpperCase()}
					</Eyebrow>
					<label
						htmlFor="project-visibility-switch"
						className="mt-2 block cursor-pointer font-display text-base font-medium text-dashboard-text"
					>
						{t("projectVisibility.label")}
					</label>
					<p className="mt-1 font-body text-sm text-dashboard-muted-dark">
						{t("projectVisibility.helper")}
					</p>
				</div>
				<Switch
					id="project-visibility-switch"
					checked={isPublic}
					disabled={isToggling}
					onCheckedChange={() => void handleToggle()}
				/>
			</div>
		</FloatPanel>
	);
}
